import { BehaviorSubject } from 'rxjs';
import { ValidationType, ValidationResultsVm } from './validation-results.model';
import { ClassificationResult } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { adviceTexts, interpolate, statusOf } from '@jump-tech-frontend/image-analysis';

export class ValidationResultsPresenter {
  vm: ValidationResultsVm;
  subject$: BehaviorSubject<ValidationResultsVm>;
  private i18ns: I18nKeys;

  setI18ns(i18ns: I18nKeys): void {
    this.i18ns = i18ns;
  }

  load(
    vmSubject$: BehaviorSubject<ValidationResultsVm>,
    results: ClassificationResult,
    type: string,
    inProgress: boolean
  ): void {
    this.subject$ = vmSubject$;

    const validationType = this.determineValidationType(results, inProgress);
    const equipmentType = this.mapTypeClassification(type);

    const hasDetections = !!(
      results?.equipmentType &&
      results.equipmentType !== '' &&
      results.equipmentType !== 'Unknown'
    );

    const reassuranceMessage = this.getReassuranceMessage(validationType);

    this.vm = {
      validationType,
      success: results?.success || inProgress,
      message: this.buildMessage(results, equipmentType, validationType),
      blockColor: this.getBlockColor(validationType),
      iconName: this.getIconName(validationType),
      title: this.getTitle(validationType),
      hasDetections,
      confidencePercentage: results?.confidence || 0,
      reassuranceMessage,
      showReassuranceMessage: !!reassuranceMessage,
      inProgress,
      showAlert: inProgress || !!results
    };

    this.subject$.next(this.vm);
  }

  private determineValidationType(result: ClassificationResult, inProgress: boolean): ValidationType {
    if (inProgress || !result) {
      return 'in_progress';
    }

    return statusOf(result);
  }

  private buildMessage(result: ClassificationResult, equipmentType: string, validationType: ValidationType): string {
    if (validationType === 'in_progress') {
      return this.inProgressMessage(equipmentType);
    }

    return adviceTexts(result, 'message', this.i18ns?.aiValidation).join(' ');
  }

  private inProgressMessage(equipmentType: string): string {
    const template = this.i18ns?.aiValidation?.qualityCheckingImage || 'Quality checking %equipmentType% image...';

    return interpolate(template, { equipmentType });
  }

  private getBlockColor(validationType: ValidationType): 'success' | 'low' | 'warning' | 'danger' | 'info' {
    switch (validationType) {
      case 'success':
        return 'success';
      case 'quality_issues':
        return 'warning';
      case 'low_confidence':
        return 'warning';
      case 'wrong_equipment':
        return 'warning';
      case 'no_detection':
        return 'info';
      case 'in_progress':
        return 'low';
      case 'error':
        return 'danger';
      default:
        return 'danger';
    }
  }

  private getIconName(
    validationType: ValidationType
  ): 'psychology' | 'check' | 'error' | 'info' | 'warning' | 'swap_horiz' {
    switch (validationType) {
      case 'success':
        return 'check';
      case 'quality_issues':
        return 'error';
      case 'low_confidence':
        return 'warning';
      case 'wrong_equipment':
        return 'swap_horiz';
      case 'no_detection':
        return 'info';
      case 'in_progress':
        return 'psychology';
      case 'error':
        return 'error';
      default:
        return 'error';
    }
  }

  private getTitle(validationType: ValidationType): string {
    const aiI18n = this.i18ns?.aiValidation;

    switch (validationType) {
      case 'success':
        return aiI18n?.imageValidationSuccessful || 'Image Validation Successful';
      case 'quality_issues':
        return aiI18n?.imageQualityIssues || 'Image Quality Issues';
      case 'low_confidence':
        return aiI18n?.lowConfidenceDetection || 'Low Confidence Detection';
      case 'wrong_equipment':
        return aiI18n?.wrongEquipmentDetected || 'Wrong Equipment Detected';
      case 'no_detection':
        return aiI18n?.noEquipmentDetected || 'No Equipment Detected';
      case 'in_progress':
        return aiI18n?.imageValidationInProgress || 'Image Validation In Progress';
      case 'error':
        return aiI18n?.imageValidationFailed || 'Image Validation Failed';
      default:
        return aiI18n?.imageValidationFailed || 'Image Validation Failed';
    }
  }

  private mapTypeClassification(type: string): string {
    const aiI18n = this.i18ns?.aiValidation;

    switch (type) {
      case 'fuse-cutout':
        return aiI18n?.fuseCutout || 'Fuse Cutout';
      case 'consumer-unit':
        return aiI18n?.consumerUnit || 'Consumer Unit';
      case 'isolator-switch':
        return aiI18n?.isolatorSwitch || 'Isolator Switch';
      default:
        return aiI18n?.unknown || 'Unknown';
    }
  }

  /**
   * Get reassurance message for quality-passed but detection-failed scenarios
   * Only shows for low_confidence, wrong_equipment and no_detection cases where the image quality is good
   */
  private getReassuranceMessage(validationType: ValidationType): string | undefined {
    if (
      validationType === 'low_confidence' ||
      validationType === 'wrong_equipment' ||
      validationType === 'no_detection'
    ) {
      return (
        this.i18ns?.aiValidation?.reassuranceMessage ||
        'You can still use this image if you think we have got this wrong.'
      );
    }
    return undefined;
  }
}
